"use client";
import { useTranslations } from "next-intl";
import { Skeleton } from "@radix-ui/themes";
import * as dc from "@/components/layout/list/defaultType";
import { DataInputParams } from "./inputType";
import { Form, Skeleton as AntSkeleton, Radio, Select, Tooltip, Flex, Button } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { Debug } from "@/util/debug";
import {
  checkDisabled,
  checkInputDirect,
  checkNoItem,
  GetDataInputElement,
  getMultiValue,
  getPlaceholder,
  getTooltip,
  getValueLabel,
  getValueList,
  getValueListSelectData,
  runFormFunction,
  setEmptyValue,
  setSelectChangeSync,
  setSelectTooltip,
} from "./inputFunction";
import { ClearButton } from "./inputButton";
import SkeletonInput from "antd/es/skeleton/Input";
import { TransitionType } from "@/components/layout/layoutType";
import { DefaultOptionType, LabeledValue, RefSelectProps } from "antd/es/select";
import { Dispatch, ReactNode, RefObject, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { getCodeGroups } from "@/util/code";
import { getKRFirstRegExp } from "@/util/arrayutil";
import { defaultAllowClear } from "../list/defaultType";
import { disableScroll, enableScroll } from "@/util/checkMobile";
import { AlertContext } from "@/components/context/alertContext";
import { checkEmpty } from "@/util/stringutil";

import Icon from "@/components/common/icon/icon";
import { Capacitor } from "@capacitor/core";
import { appInputScroll, isNativePlatform } from "@/util/apputil";
// import dynamic from "next/dynamic";
// const Icon = dynamic(() => import("@/components/common/icon/icon"), { ssr: false });

const GetInput = (
  t: TransitionType,
  params: DataInputParams,
  valueList: DefaultOptionType[] | undefined,
  loading: boolean,
  setTooltip: Dispatch<any>,
  selectRef: RefObject<RefSelectProps | null>,
  isNativePlatform: boolean,
  open: boolean,
  search: boolean,
  showSearch: boolean,
  setOpen: Dispatch<SetStateAction<boolean>>,
) => {
  const unitPrev = params.unitPrev ? GetDataInputElement(params.unitPrev, false, params.values, t) : undefined;
  const unit = params.unit ? GetDataInputElement(params.unit, true, params.values, t) : undefined;
  const defaultValue = getMultiValue(params, params.defaultValue ?? params.value);
  const disabled = checkDisabled(params);
  const placeholder =
    getPlaceholder(t, params, disabled) ??
    (params.value
      ? getValueLabel(getMultiValue(params, params.value), valueList)
      : defaultValue !== undefined && valueList !== undefined
        ? getValueLabel(defaultValue, valueList)
        : undefined);
  return (
    <Select
      ref={selectRef}
      className={cn(
        params.inputClassName,
        isNativePlatform && showSearch ? "ant-select-keybtn-change" : "",
        params.readOnly ? "readOnly" : "",
        loading ? "loading" : "",
        valueList === undefined || (loading && params.dataUrl) ? "emptyValueList" : "",
      )}
      style={
        params.readOnly
          ? params.inputStyle
            ? { ...params.inputStyle, pointerEvents: "none" }
            : { pointerEvents: "none" }
          : params.inputStyle
      }
      open={open}
      showSearch={search}
      optionFilterProp={(params.selectProp?.optionFilterProp ?? showSearch) ? "label" : undefined}
      filterOption={(input, option) => {
        const label: string = (option?.label_str ?? option?.label ?? "").toString();
        const regex = getKRFirstRegExp(input);
        if (regex) {
          return regex.test(label);
        } else {
          return label.toLowerCase().includes(input.toLowerCase());
        }
      }}
      // prefix={
      //   isNativePlatform && showSearch ? (
      //     <span
      //       onClick={(e) => {
      //         setOpen(false);
      //         e.stopPropagation();
      //         e.preventDefault();
      //         selectRef.current?.blur();
      //         setSearch((prev) => {
      //           return !prev;
      //         });
      //       }}
      //       style={{
      //         display: "flex",
      //         cursor: "pointer",
      //         pointerEvents: "auto", // 중요: 클릭 가능
      //         opacity: search ? 1 : 0.3,
      //       }}
      //     >
      //       <Icon iconName="Keyboard" iconType="" />
      //     </span>
      //   ) : undefined
      // }
      placeholder={placeholder}
      value={getMultiValue(params, params.value ?? params.defaultValue)}
      disabled={disabled}
      options={valueList}
      allowClear={
        (params.allowClear ?? defaultAllowClear)
          ? {
              clearIcon: (
                <CloseCircleFilled
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              ),
            }
          : undefined
      }
      loading={loading}
      //variant={params.variant ?? (params.inputMode === "view" && params.selectProp?.mode === undefined ? dc.viewVariant : undefined)}
      variant={params.variant ?? (params.inputMode === "view" ? dc.viewVariant : undefined)}
      // onSelect={
      //   params.selectProp?.onSelect ??
      //   ((value: any, option: DefaultOptionType) => {
      //     Debug(value, option);
      //   })
      // }
      // virtual={false}
      onClick={params.selectProp?.onClick ?? params.onClick}
      onOpenChange={(e) => {
        appInputScroll(selectRef, e, true, search, params.scrollMargin);
        if (params.selectProp?.onOpenChange) {
          params.selectProp.onOpenChange(e);
        }
        setOpen(e);
      }}
      onChange={(e: any, option) => {
        checkInputDirect(params);
        if (typeof params.tooltip === "boolean" && params.tooltip) {
          if (checkEmpty(e)) {
            setTooltip(undefined);
          } else {
            if (option instanceof Array) {
              setTooltip(
                option
                  .map((item) => {
                    return item.label_str ?? item.label;
                  })
                  .join(","),
              );
            } else {
              setTooltip(option?.label_str ?? option?.label);
            }
          }
        }
        if (params.selectChangeSync) {
          setSelectChangeSync(params);
        }
        if (params.selectChangeLoad) {
          const temp_list = typeof params.selectChangeLoad === "string" ? [params.selectChangeLoad] : params.selectChangeLoad;
          temp_list.forEach((item) => {
            if (params.selectChangeDelay) {
              setTimeout(() => {
                runFormFunction(params.formFunction, item, "changeLoad");
              }, params.selectChangeDelay);
            } else {
              runFormFunction(params.formFunction, item, "changeLoad");
            }
            // if (tempFunction) {
            //   tempFunction();
            // } else {
            //   //Debug(item, tempFunction);
            // }
          });
        }
        if (params.onChange) {
          params.onChange({ key: params.name, value: e, index: params.index, option });
        }
      }}
      suffixIcon={params.readOnly || disabled ? "" : undefined}
      {...params.selectProp}
      // multiple` or `tags`
      maxCount={params.selectProp?.mode && ["multiple", "tags"].includes(params.selectProp.mode) ? params.selectProp?.maxCount : undefined}
    />
  );
};

export default function DataSelect(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const ta = useTranslations("alert");
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;
  const disabled = checkDisabled(params);
  const [tooltip, setTooltip] = useState(getTooltip(t, params));
  const [valueList, setValueList] = useState<DefaultOptionType[] | undefined>(() => {
    // Debug("Data Select init", params.name, params);
    return getValueList("init", params, t, undefined);
  });
  const [loading, setLoading] = useState(params.dataUrl ? true : (params.loading ?? false));
  const showSearch = disabled ? false : params.inputType == "selectSearch" ? true : false;
  const selectRef = useRef<RefSelectProps>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<boolean>(isNativePlatform ? false : showSearch);
  useEffect(() => {
    const checkSearch = disabled ? false : params.inputType == "selectSearch" ? true : false;
    setSearch(isNativePlatform ? false : checkSearch);
  }, [disabled, params.inputType]);

  const alertContext = useContext(AlertContext);

  const reload = () => {
    // Debug("Reload getValueList");
    if (params.selectPropOption?.reloadButtonClick) {
      setLoading(true);
      params.selectPropOption.reloadButtonClick(setValueList, setLoading);
    } else if (params.codeGroup) {
      setLoading(true);
      getCodeGroups(
        [params.codeGroup],
        params.locale,
        undefined,
        (data: Record<string, any[]>) => {
          //Debug(data);
          if (params.codeGroup && params.codeGroups) {
            params.codeGroups[params.codeGroup] = data[params.codeGroup];
            setValueList(getValueList("reload", params, t, undefined));
          }
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        },
        true,
      );
    } else if (params.dataUrl) {
      setValueList(getValueList("reload", params, t, true));
    }
  };
  const getNowValueList = () => {
    const nowValue = params.form?.getFieldInstance(params.name) ? params.form.getFieldValue(params.name) : params.value;
    if (nowValue !== undefined) {
      if (nowValue instanceof Array) {
        return valueList?.filter((item) => nowValue.includes(item.value));
      } else {
        valueList?.forEach((item) => {
          if (item.value === nowValue) {
            return item;
          }
        });
        return undefined;
      }
    }
    return undefined;
  };

  const getNowValueLabel = () => {
    const nowValueList = getNowValueList();
    // Debug(params.name, nowValueList);
    if (nowValueList !== undefined && nowValueList.length > 1) {
      return nowValueList.map((item) => {}).join(params.valueJoin ?? ",");
    } else {
      return undefined;
    }
  };

  // const setTooltipNow = () => {};
  const changeLoad = (loadAfter?: Function) => {
    setTimeout(() => {
      const valueList = getValueList("changeLoad", params, t, undefined, loadAfter);
      setValueList(valueList);
    }, dc.setTimeoutShort);
  };
  const selectData = () => {
    return getValueListSelectData(params);
  };

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    const temp_function = params.formFunction[params.name];
    if (temp_function) {
      temp_function["reload"] = reload;
      temp_function["changeLoad"] = changeLoad;
      temp_function["checkInputDirect"] = (focus?: boolean) => {
        checkInputDirect(params, focus);
      };

      temp_function["selectData"] = selectData;
      temp_function["setSelectLoading"] = setLoading;

      temp_function["getValueList"] = () => {
        return valueList;
      };
      temp_function["setValueList"] = (valueList: DefaultOptionType[] | undefined) => {
        setValueList(valueList);
      };
      temp_function["setTooltip"] = setTooltip;
      temp_function["checkNowValue"] = () => {
        if (
          params.form?.getFieldInstance(params.name) &&
          params.form.getFieldValue(params.name) !== undefined &&
          getNowValueList() === undefined
        ) {
          params.form.getFieldValue(undefined);
        }
      };
      temp_function["setNowValueTooltip"] = () => {
        // Debug("start setNowValueTooltip");
        setSelectTooltip(params, valueList);
        //setTooltip(getNowValueLabel());
      };
      temp_function["setEmptyValue"] = () => {
        setEmptyValue(params, valueList);
      };
    }
  }
  useEffect(() => {
    if (params.formMemo && params.name) {
      if (!params.formMemo[params.name]) {
        params.formMemo[params.name] = {};
      }
      const temp_memo = params.formMemo[params.name];
      temp_memo.valueJoin = params.valueJoin;
      temp_memo.inputType = params.inputType;
      temp_memo.selectMode = params.selectProp?.mode;
      if (params.inputDirect) {
        temp_memo.inputDirect = params.inputDirect;
      } else {
        delete temp_memo.inputDirect;
      }
    }
  }, [params.formMemo, params.name, params.valueJoin, params.inputDirect, params.selectProp?.mode, params.inputType]);

  useEffect(() => {
    if (params.setValueList && params.name) {
      params.setValueList[params.name] = setValueList;
    }
  }, [params.name, params.setValueList, setValueList]);

  useEffect(() => {
    if (params.codeGroup) {
      // Debug("Select changeGroup", params.name);
      setValueList(getValueList("changeGroup", params, t, undefined));
    } else if (params.dataUrl) {
      if (params.loading !== loading) {
        setLoading(params.loading ?? false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    //params,
    params.locale,
    params.valueList,
    params.codeGroup,
    params.codeGroups,
    params.codeGroupLabel,
    params.codeGroupJoinPrev,
    params.codeGroupJoinNext,
    params.dataProp,
    params.dataUrl,
    params.loading,
    params.userSeq,
    params.inputDirect,
  ]);

  useEffect(() => {
    const change_tooltip = getTooltip(t, params);
    // Debug("change tooltip", change_tooltip, params.name);
    setTooltip(change_tooltip);
  }, [t, params, params.tooltip]);
  useEffect(() => {
    if (params.inputDirect) {
      //alert(params.value);
      checkInputDirect(params, false);
    }
  }, [params, params.value, params.inputDirect]);
  useEffect(() => {
    if (params.dataValueList) {
      Debug("Select change select valuelist params Values", params.name, params.values);
      if (params.initFunction?.current) {
        params.initFunction.current.push(() => {
          setValueList(getValueList("changeValues", params, t, undefined));
        });
      } else {
        setValueList(getValueList("changeValues", params, t, undefined));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.dataValueList, params.values]);

  const ReloadButton = () => {
    //const ReloadButtonCheck = params.selectProp?.reloadButton ?? false;

    if (
      (params.inputMode !== "view" && (params.selectPropOption?.reloadButton ?? (params.codeGroup || params.dataUrl ? true : false))) ||
      (params.inputMode === "view" && (params.selectPropOption?.reloadButton ?? false))
    ) {
      //const t = useTranslations(params.transitionGroup ?? "contents");
      const tooltip = params.selectPropOption?.reloadTitle
        ? (params.selectPropOption?.reloadTitleTransition ?? true)
          ? t(params.selectPropOption.reloadTitle)
          : params.selectPropOption.reloadTitle
        : t("reload");
      return (
        <Tooltip color={dc.tooltipColor} title={tooltip} placement={params.tooltipPlacement} {...params.tooltipProps}>
          <Button
            icon={<Icon iconName={params.selectPropOption?.reloadButtonName ?? "ArrowClockwise"} iconType="" />}
            onClick={() => {
              if (params.userType && (params.userType.includes("ADMIN") || params.userType.includes("SADMIN"))) {
                reload();
              } else {
                const alertP = alertContext.setProp;
                if (alertP) {
                  alertP({
                    open: true,
                    title: ta("refresh"),
                    description: ta("refresh_select_detail"),
                    useCancel: true,
                    useOk: true,
                    okFunction: () => {
                      reload();
                    },
                  });
                }
              }
            }}
            className={cn(
              params.selectPropOption?.reloadButtonFront ? "ml-2" : "ml-2",
              disabled ? "invisible" : "min-w-[2rem]",
              "with_select my-auto",
              params.selectPropOption?.reloadButtonClassName,
            )}
            id={params.name + "_reloadButton"}
            name={params.name + "_reloadButton"}
            disabled={loading}
            type={params.selectPropOption?.reloadButtonType}
          />
        </Tooltip>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Tooltip color={dc.tooltipColor} title={tooltip} placement={params.tooltipPlacement} {...params.tooltipProps}>
      <Flex className={cn((params.widthFull ?? false) ? "w-full" : "")}>
        {params.clearProp?.clearButton && (params.clearProp?.clearButtonFront ?? false) ? <ClearButton {...params} /> : ""}
        {prev}
        {params.selectPropOption?.reloadButtonFront ? <ReloadButton /> : ""}
        {checkNoItem(params) ? (
          GetInput(t, params, valueList, loading, setTooltip, selectRef, isNativePlatform, open, search, showSearch, setOpen)
        ) : (
          <Form.Item
            name={params.name}
            noStyle
            initialValue={getMultiValue(params, params.value ?? params.defaultValue)}
            required={params.required}
          >
            {GetInput(t, params, valueList, loading, setTooltip, selectRef, isNativePlatform, open, search, showSearch, setOpen)}
          </Form.Item>
        )}
        {isNativePlatform && showSearch ? (
          <span
            id={params.name + "_keybtn"}
            className={"ant-select-keybtn"}
            style={{ opacity: search ? 1 : 0.3 }}
            onClick={(e) => {
              setOpen(false);
              e.stopPropagation();
              e.preventDefault();
              selectRef.current?.blur();
              setSearch((prev) => {
                return !prev;
              });
            }}
          >
            <Icon iconName="Keyboard" iconType="" />
          </span>
        ) : (
          ""
        )}
        {params.selectPropOption?.reloadButtonFront ? "" : <ReloadButton />}
        {next}
        {params.clearProp?.clearButton && !(params.clearProp?.clearButtonFront ?? false) ? <ClearButton {...params} /> : ""}
      </Flex>
    </Tooltip>
  );
}
